import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { passwordSchema, type PasswordFormData } from '../../lib/validators';
import { resetPasswordApi } from '../../services/api';
import { Button } from '../../components/ui/Button';
import { PasswordInput } from '../../components/ui/PasswordInput';
import { PasswordRequirements } from '../../components/shared/PasswordRequirements';
import { getErrorMessage, extractApiError } from '../../lib/errorCodes';
import { toast } from 'sonner';

export default function SubscriptionPage() {
  const { t } = useTranslation();
  const { id, token } = useParams<{ id: string; token: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  const password = watch('password');
  const confirmPassword = watch('confirmPassword');

  const onSubmit = async (data: PasswordFormData) => {
    if (!token) return;
    setIsLoading(true);
    try {
      const result = await resetPasswordApi(token, data.password);
      if (result.errorCode) {
        toast.error(getErrorMessage(result.errorCode, 'apiErrors.invalidToken'));
      } else {
        toast.success(t('general.accountActivated'));
        setTimeout(() => navigate('/'), 2000);
      }
    } catch (error) {
      toast.error(extractApiError(error));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {t('general.subscriptionTitle')}
        </h2>
        <p className="text-gray-600">{t('general.subscriptionDesc')}</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <PasswordInput
          label={t('general.passwordLabel')}
          placeholder="••••••••"
          autoComplete="new-password"
          error={errors.password?.message}
          {...register('password')}
        />
        <PasswordInput
          label={t('general.confirmPassword')}
          placeholder="••••••••"
          autoComplete="new-password"
          error={errors.confirmPassword?.message}
          {...register('confirmPassword')}
        />
        {password && <PasswordRequirements password={password} confirmPassword={confirmPassword} />}
        <Button type="submit" isLoading={isLoading}>
          {t('general.createButton')}
        </Button>
      </form>

      <div className="mt-4 text-center">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="text-sm text-primary hover:text-secondary transition-colors"
        >
          {t('general.backToLogin')}
        </button>
      </div>
    </>
  );
}
